import * as Sentry from '@sentry/nextjs';

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

// Minified React error #418 (and its recoverable-render siblings #423/#425) arrives in
// GlitchTip stackless, with `culprit: null` — Next routes React's recoverable errors through
// `reportGlobalError` with no frames, so NO amount of source-map work will ever produce a
// culprit for it. Uploading maps is already configured; that is not the missing piece.
//
// WHAT WE KNOW (investigated 2026-07-28, ~13 events/24h across ~8 routes in admin-dashboard
// and landing-page — 1-3 per route per day, longstanding, not a regression):
//
//  * `args[]=HTML` is NOT the <html> element. It is React's own discriminator between a
//    STRUCTURAL mismatch and a text one: `throwOnHydrationMismatch` in
//    node_modules/next/dist/compiled/react-dom/cjs/react-dom-client.production.js passes
//    "text" or "HTML" as arg 1. Ours says HTML, so an element/node differed — a differently
//    formatted date, price or locale string is by construction a TEXT mismatch and would
//    report `args[]=text`. Date/currency/timezone fixes are placebos here.
//  * Nothing in this app's root tree reads a browser-only value during render — every
//    such read is deferred to useEffect, and the one clock-driven component
//    (components/UTBKCountdown.tsx, `useState<TimeLeft | null>(null)` with a comment
//    saying exactly why) is already hardened against this. On the admin side, every
//    affected route prerenders to a content-free auth splash with no dates or currency
//    in the HTML at all, so a single formatting cause cannot explain both surfaces.
//  * `suppressHydrationWarning` on <html> would be a NO-OP: in the production build it is
//    consulted only on the two text paths, never for structural mismatches. It is not the
//    fix for this despite being the usual advice.
//  * Extension attributes stamped on <html>/<body> (Grammarly, password managers, dark
//    readers) also cannot produce this in production — attribute mismatches take a
//    dev-only, non-throwing path — and nodes injected directly INTO html/head/body are
//    exempt from the leftover-node throw (HostSingleton). So the surviving explanation is a
//    structural mutation strictly INSIDE a nested element: Google/Chrome Translate wrapping
//    text in <font>, Cloudflare Email Obfuscation rewriting a mailto text node, Rocket
//    Loader rewriting inline <script>, or a content blocker deleting a node.
//
// So: no component change and NO `ignoreErrors` entry. Errors are unsampled, 13/day costs
// nothing, and suppressing an unexplained user-facing failure before we know what it is
// buries the only signal we have. Instead, make the error self-diagnosing. These tags are
// structural markers only — attribute NAMES, tag names and booleans, never values, never
// outerHTML — so nothing widens the PII we already store.
//
// REVISIT: if a mutator tag comes back true, THEN a narrow ignoreErrors is justified
// (we would finally know what we are hiding). If the tags come back clean and the rate
// rises, escalate — it would mean the mutation is ours after all.
// Caveat: this runs after React has re-rendered client-side, so a transient injected node
// may already be gone. That is why these probes are document-level markers that persist.
const REACT_HYDRATION_ERROR = /Minified React error #4(18|23|25)/;

function hydrationForensics(): Record<string, string | boolean> {
  const has = (selector: string): boolean => {
    try {
      return !!document.querySelector(selector);
    } catch {
      return false;
    }
  };
  const html = document.documentElement;
  const body = document.body;
  return {
    htmlAttrs: html.getAttributeNames().join(','),
    bodyAttrs: body ? body.getAttributeNames().join(',') : '',
    bodyKids: body ? Array.from(body.children).map((el) => el.tagName).join(',') : '',
    lang: navigator.language,
    gTranslate: /translated-(ltr|rtl)/.test(html.className),
    msTranslate: has('font[_msttexthash]'),
    cfEmail: has('.__cf_email__'),
    rocketLoader: has('script[type="text/rocketloader"]'),
    darkReader: has('[data-darkreader-mode]'),
    grammarly: has('[data-lt-installed]'),
  };
}

// Client-side init. No-op when NEXT_PUBLIC_SENTRY_DSN is unset.
// No tracing here (no tracesSampleRate, no onRouterTransitionStart export): nothing read
// the browser trace data. Error capture is unaffected; server/edge tracing in
// instrumentation.ts is unchanged. This does NOT shrink the bundle: under Turbopack,
// @sentry/nextjs 10.67 ignores bundleSizeOptimizations, so the SDK ships as before.
if (dsn) {
  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,
    release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
    beforeSend(event) {
      const message = event.message ?? event.exception?.values?.[0]?.value ?? '';
      if (!REACT_HYDRATION_ERROR.test(message)) return event;
      if (typeof document === 'undefined') return event;
      try {
        event.tags = { ...event.tags, ...hydrationForensics() };
      } catch {
        // Diagnostics must never cost us the event itself.
      }
      return event;
    },
  });
}
